import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Error caught by boundary:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Flex minH="100vh" alignItems="center" justifyContent="center" bg="volyx.lightGray" p="5">
          <Box maxW="md" w="full" bg="white" borderRadius="2xl" boxShadow="lg" p={{ base: '6', md: '8' }}>
            <Flex alignItems="center" justifyContent="center" mb="4">
              <Box color="volyx.error">
                <AlertTriangle size={48} />
              </Box>
            </Flex>
            <Text
              as="h2"
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="semibold"
              color="volyx.text"
              mb="3"
              textAlign="center"
            >
              Something went wrong
            </Text>
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              color="volyx.textSecondary"
              mb="6"
              textAlign="center"
            >
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </Text>
            {import.meta.env.DEV && this.state.error && (
              <Box as="details" mb="4" p="3" bg="volyx.lightGray" borderRadius="lg" fontSize="xs">
                <Box as="summary" cursor="pointer" fontWeight="medium" color="volyx.text" mb="2">
                  Error Details (Development Only)
                </Box>
                <Box as="pre" overflow="auto" color="volyx.textSecondary">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Box>
              </Box>
            )}
            <Flex gap="3">
              <Box
                as="button"
                onClick={this.handleReset}
                flex="1"
                px="4"
                py="2.5"
                bg="volyx.primary"
                color="white"
                borderRadius="lg"
                fontWeight="medium"
                border="none"
                cursor="pointer"
                transition="background 0.2s"
                _hover={{ opacity: 0.9 }}
              >
                Try Again
              </Box>
              <Box
                as="button"
                onClick={() => {
                  try {
                    this.handleReset();
                  } catch {
                    window.location.reload();
                  }
                }}
                flex="1"
                px="4"
                py="2.5"
                bg="volyx.lightGray"
                color="volyx.text"
                borderRadius="lg"
                fontWeight="medium"
                border="none"
                cursor="pointer"
                transition="background 0.2s"
                _hover={{ bg: 'volyx.mediumGray' }}
              >
                Reload Page
              </Box>
            </Flex>
          </Box>
        </Flex>
      );
    }

    return this.props.children;
  }
}
